import { SimpleProject } from '../models/simple-project';

// Bump when the SimpleProject shape changes so stale caches are ignored
// rather than rehydrated into an incompatible structure.
export const PLAN_LIST_CACHE_VERSION = 2;

// Minimal storage surface. window.localStorage satisfies this; tests pass a fake.
export interface KeyValueStore {
    getItem(key: string): string | null;
    setItem(key: string, value: string): void;
    removeItem(key: string): void;
}

export interface CachedPlanLists {
    myProjects: SimpleProject[];
    sharedProjects: SimpleProject[];
    deletedProjects: SimpleProject[];
}

interface StoredPlanListCache {
    version: number;
    savedAtUnix: number;
    myProjects: any[];
    sharedProjects: any[];
    deletedProjects: any[];
}

function rehydrate(items: any[] | undefined): SimpleProject[] {
    if (!items) {
        return [];
    }
    return items.map(data => {
        const id = data && data.id != null ? String(data.id) : '';
        return Object.assign(new SimpleProject(id), data);
    });
}

export class PlanListCache {
    constructor(
        private store: KeyValueStore,
        private nowUnix: () => number,
    ) {
    }

    private keyFor(email: string): string {
        return `_gp_plan_list_cache_${email}`;
    }

    read(email: string): CachedPlanLists | null {
        if (!email) {
            return null;
        }
        const raw = this.store.getItem(this.keyFor(email));
        if (!raw) {
            return null;
        }
        let parsed: StoredPlanListCache;
        try {
            parsed = JSON.parse(raw);
        } catch (e) {
            return null;
        }
        if (!parsed || parsed.version !== PLAN_LIST_CACHE_VERSION) {
            return null;
        }
        return {
            myProjects: rehydrate(parsed.myProjects),
            sharedProjects: rehydrate(parsed.sharedProjects),
            deletedProjects: rehydrate(parsed.deletedProjects),
        };
    }

    write(email: string, lists: CachedPlanLists): void {
        if (!email) {
            return;
        }
        const payload: StoredPlanListCache = {
            version: PLAN_LIST_CACHE_VERSION,
            savedAtUnix: this.nowUnix(),
            myProjects: lists.myProjects || [],
            sharedProjects: lists.sharedProjects || [],
            deletedProjects: lists.deletedProjects || [],
        };
        try {
            this.store.setItem(this.keyFor(email), JSON.stringify(payload));
        } catch (e) {
            // Storage full or unavailable — caching is best-effort, never fatal.
        }
    }

    clear(email: string): void {
        if (!email) {
            return;
        }
        this.store.removeItem(this.keyFor(email));
    }
}
